import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Calendar, MapPin, ChefHat, DollarSign, Check } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { USE_MOCK_DATA } from '../services/mockData';

export function EventDetailModal({ event, user, onClose }) {
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    const checkRegistration = async () => {
      if (!user || !event) return;

      try {
        if (USE_MOCK_DATA) {
          const registrations = JSON.parse(localStorage.getItem('eventRegistrations') || '[]');
          const registered = registrations.some(r => r.eventId === event.id && r.userId === user.uid);
          setIsRegistered(registered);
          return;
        }

        const q = query(
          collection(db, 'eventRegistrations'),
          where('eventId', '==', event.id),
          where('userId', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);
        setIsRegistered(!querySnapshot.empty);
      } catch (error) {
        console.error('Error checking registration:', error);
      }
    };

    if (event && user) {
      checkRegistration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, user]);

  const handleRegister = async () => {
    if (!user) {
      // Could open login modal here
      return;
    }

    if (isRegistered) {
      return;
    }

    setRegistering(true);
    try {
      if (USE_MOCK_DATA) {
        const registrations = JSON.parse(localStorage.getItem('eventRegistrations') || '[]');
        registrations.push({
          eventId: event.id,
          userId: user.uid,
          registeredAt: new Date().toISOString()
        });
        localStorage.setItem('eventRegistrations', JSON.stringify(registrations));
        setIsRegistered(true);
        setShowPayment(false);
        setRegistering(false);
        return;
      }

      await addDoc(collection(db, 'eventRegistrations'), {
        eventId: event.id,
        userId: user.uid,
        userEmail: user.email,
        registeredAt: new Date(),
        status: 'confirmed'
      });

      setIsRegistered(true);
      setShowPayment(false);
    } catch (error) {
      console.error('Error registering:', error);
    } finally {
      setRegistering(false);
    }
  };

  const handleAddToCalendar = () => {
    if (!event) return;

    const startDate = new Date(event.date);
    const [hours, minutes] = event.time.split(':');
    startDate.setHours(parseInt(hours), parseInt(minutes));
    const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000); // 3 hours

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.address || event.location)}`;

    window.open(calendarUrl, '_blank');
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getEventDate = () => {
    if (!event || !event.date) return new Date();
    return event.date instanceof Date ? event.date : (event.date.toDate ? event.date.toDate() : new Date(event.date));
  };

  if (!event) return null;

  const spotsLeft = event.capacity - (event.registered || 0);
  const eventDate = getEventDate();
  const daysUntil = Math.ceil((eventDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto"
        onClick={onClose}
      >
        <div className="min-h-screen px-4 py-8 flex items-start justify-center">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className="bg-background max-w-5xl w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-10 w-10 h-10 border border-border hover:border-foreground bg-background flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Hero Image */}
            <div className="relative h-96 overflow-hidden">
              <ImageWithFallback
                src={event.imageUrl}
                alt={event.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>

              {/* Days Away Badge */}
              {daysUntil > 0 && (
                <div className="absolute top-6 left-6 bg-background px-4 py-2 border border-border">
                  <p className="text-xs uppercase tracking-widest">
                    {daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days away`}
                  </p>
                </div>
              )}

              {/* Spots Left Badge */}
              {spotsLeft <= 5 && spotsLeft > 0 && (
                <div className="absolute top-6 right-6 bg-foreground text-background px-4 py-2">
                  <p className="text-xs uppercase tracking-widest">Only {spotsLeft} spots left</p>
                </div>
              )}

              {spotsLeft === 0 && (
                <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                  <p className="text-white text-xl uppercase tracking-widest">Sold Out</p>
                </div>
              )}
            </div>

            <div className="p-12">
              {/* Header */}
              <div className="mb-12">
                <h1 className="serif text-6xl mb-4">{event.title}</h1>
                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ChefHat className="w-4 h-4" />
                    <span>{event.chef}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>{event.restaurant}</span>
                  </div>
                </div>

                {/* Event Details Grid */}
                <div className="grid grid-cols-3 gap-8 pb-8 border-b border-border">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Date</p>
                      <p className="text-sm">{formatDate(getEventDate())}</p>
                      <p className="text-xs text-muted-foreground">{event.time}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Location</p>
                      <p className="text-sm">{event.location}</p>
                      {event.address && (
                        <p className="text-xs text-muted-foreground">{event.address}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <DollarSign className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Price</p>
                      <p className="serif text-2xl">${event.price}</p>
                      <p className="text-xs text-muted-foreground">per person</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-12">
                <h2 className="serif text-4xl mb-4">About This Event</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">{event.description}</p>

                {event.chefBio && (
                  <div className="bg-stone-50 p-6 border border-border">
                    <h3 className="serif text-2xl mb-3">About {event.chef}</h3>
                    <p className="text-muted-foreground leading-relaxed">{event.chefBio}</p>
                  </div>
                )}
              </div>

              {/* Menu */}
              {event.menu && event.menu.length > 0 && (
                <div className="mb-12">
                  <h2 className="serif text-4xl mb-6">Menu</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {event.menu.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 border border-border p-4">
                        <span className="text-xl">🍽️</span>
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* What's Included */}
              {event.includes && event.includes.length > 0 && (
                <div className="mb-12">
                  <h2 className="serif text-4xl mb-6">What's Included</h2>
                  <div className="space-y-3">
                    {event.includes.map((item, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-foreground text-background flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4" />
                        </div>
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Registration Section */}
              <div className="border-t border-border pt-12">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="serif text-4xl mb-2">Join This Event</h2>
                    <p className="text-sm text-muted-foreground">
                      {event.registered || 0} / {event.capacity} registered
                      {spotsLeft > 0 && ` · ${spotsLeft} spots available`}
                    </p>
                  </div>
                </div>

                {!isRegistered && spotsLeft > 0 && (
                  <>
                    {!showPayment ? (
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setShowPayment(true)}
                          className="flex-1 h-14 bg-foreground text-background hover:bg-foreground/90 transition-colors text-sm uppercase tracking-widest"
                        >
                          Register Now - ${event.price}
                        </button>
                        <button
                          onClick={handleAddToCalendar}
                          className="px-6 h-14 border border-foreground hover:bg-foreground hover:text-background transition-all text-sm uppercase tracking-widest"
                        >
                          <Calendar className="w-4 h-4 inline mr-2" />
                          Add to Calendar
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="bg-stone-50 p-6 border border-border">
                          <p className="text-sm text-muted-foreground mb-4">
                            In production, this would integrate with a payment processor like Stripe.
                            For now, registration is free in demo mode.
                          </p>
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => setShowPayment(false)}
                              className="px-6 h-12 border border-border hover:bg-foreground hover:text-background transition-all text-xs uppercase tracking-widest"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleRegister}
                              disabled={registering}
                              className="flex-1 h-12 bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest"
                            >
                              {registering ? 'Processing...' : 'Confirm Registration'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {isRegistered && (
                  <div className="bg-green-50 border border-green-200 p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-green-600 text-white flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8" />
                    </div>
                    <h3 className="serif text-3xl mb-2">You're Registered!</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Check your email for confirmation and event details.
                    </p>
                    <button
                      onClick={handleAddToCalendar}
                      className="px-6 h-12 border border-foreground hover:bg-foreground hover:text-background transition-all text-xs uppercase tracking-widest"
                    >
                      <Calendar className="w-4 h-4 inline mr-2" />
                      Add to Calendar
                    </button>
                  </div>
                )}

                {spotsLeft === 0 && !isRegistered && (
                  <div className="bg-stone-50 border border-border p-8 text-center">
                    <p className="text-sm text-muted-foreground mb-4">
                      This event is sold out. Join the waitlist to be notified if spots become available.
                    </p>
                    <button className="px-6 h-12 border border-foreground hover:bg-foreground hover:text-background transition-all text-xs uppercase tracking-widest">
                      Join Waitlist
                    </button>
                  </div>
                )}

                {!user && (
                  <div className="bg-stone-50 border border-border p-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      Please <a href="/login" className="text-foreground underline">login</a> or{' '}
                      <a href="/signup" className="text-foreground underline">sign up</a> to register for this event.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

